import { useState, useEffect } from 'react'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { isAuthenticated } from '@/lib/storage'
import { onAuthExpired } from '@/lib/api-client'
import { isOfficeReady } from '@/lib/office'
import { Header } from '@/components/Header'
import { EmptyState } from '@/components/EmptyState'
import { Spinner } from '@/components/Spinner'
import { PairingScreen } from '@/screens/PairingScreen'
import { MatterPickerScreen } from '@/screens/MatterPickerScreen'
import { EmailPreviewScreen } from '@/screens/EmailPreviewScreen'
import { ConfirmationScreen } from '@/screens/ConfirmationScreen'
import { SettingsScreen } from '@/screens/SettingsScreen'
import { ComposeScreen } from '@/screens/ComposeScreen'
import { useCurrentItem } from '@/hooks/use-current-item'
import { isComposeMode } from '@/lib/office'
import type { Matter } from '@/types'
import { Mail } from 'lucide-react'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

type Screen = 'pairing' | 'picker' | 'preview' | 'confirmation' | 'settings' | 'compose'

function AppInner() {
  const [authed, setAuthed] = useState(() => isAuthenticated())
  const [screen, setScreen] = useState<Screen>(() => {
    if (!isAuthenticated()) return 'pairing'
    return isComposeMode() ? 'compose' : 'picker'
  })
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null)

  const { item, isLoading: itemLoading } = useCurrentItem()

  // Subscribe to auth expiration events from the API client
  useEffect(() => {
    const unsub = onAuthExpired(() => {
      queryClient.clear()
      setAuthed(false)
      setScreen('pairing')
      setSelectedMatter(null)
    })
    return unsub
  }, [])

  function handlePaired() {
    setAuthed(true)
    setScreen(isComposeMode() ? 'compose' : 'picker')
  }

  function handleSelectMatter(matter: Matter) {
    setSelectedMatter(matter)
    setScreen('preview')
  }

  function handleLinked(_emailId: string) {
    setScreen('confirmation')
  }

  function handleLinkAnother() {
    setSelectedMatter(null)
    queryClient.invalidateQueries({ queryKey: ['matters'] })
    setScreen('picker')
  }

  function handleSignOut() {
    setAuthed(false)
    setScreen('pairing')
    setSelectedMatter(null)
    queryClient.clear()
  }

  const showSettings = screen !== 'pairing' && screen !== 'compose'
  const showBack = screen === 'settings'

  // Not paired yet
  if (!authed || screen === 'pairing') {
    return (
      <div className="flex flex-col h-screen bg-white max-w-sm mx-auto">
        <Header showSettings={false} />
        <div className="flex-1 overflow-hidden">
          <PairingScreen onPaired={handlePaired} />
        </div>
        <Toaster richColors position="bottom-center" />
      </div>
    )
  }

  // Settings overlay
  if (screen === 'settings') {
    return (
      <div className="flex flex-col h-screen bg-white max-w-sm mx-auto">
        <Header showSettings={false} />
        <div className="flex items-center gap-2 px-4 py-2 border-b border-secondary-100">
          <button
            onClick={() => setScreen('picker')}
            className="text-xs text-primary-500 hover:text-primary-600 transition-colors"
          >
            ← Back
          </button>
          <span className="text-sm font-medium text-secondary-700">Settings</span>
        </div>
        <div className="flex-1 overflow-hidden">
          <SettingsScreen onSignOut={handleSignOut} />
        </div>
        <Toaster richColors position="bottom-center" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen bg-white max-w-sm mx-auto">
      <Header
        showSettings={showSettings && !showBack}
        onSettings={() => setScreen('settings')}
      />

      <div className="flex-1 overflow-hidden">
        {/* No email selected */}
        {screen === 'picker' && !itemLoading && !item && !isOfficeReady() && (
          <EmptyState
            icon={<Mail size={28} />}
            title="No email selected"
            description="Open or select an email in Outlook to get started."
          />
        )}

        {screen === 'picker' && (
          <MatterPickerScreen onSelect={handleSelectMatter} />
        )}

        {screen === 'preview' && selectedMatter && (
          <>
            {itemLoading ? (
              <div className="flex justify-center items-center h-full">
                <Spinner size={24} />
              </div>
            ) : item ? (
              <EmailPreviewScreen
                matter={selectedMatter}
                email={item}
                onBack={() => setScreen('picker')}
                onLinked={handleLinked}
              />
            ) : (
              <EmptyState
                icon={<Mail size={28} />}
                title="No email selected"
                description="Switch to an email in Outlook to preview and link it."
              />
            )}
          </>
        )}

        {screen === 'confirmation' && selectedMatter && (
          <ConfirmationScreen
            matter={selectedMatter}
            onLinkAnother={handleLinkAnother}
          />
        )}

        {screen === 'compose' && (
          <ComposeScreen />
        )}
      </div>

      <Toaster richColors position="bottom-center" />
    </div>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppInner />
    </QueryClientProvider>
  )
}
